import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { KeyboardEventKey } from "../../constants";
import { TId } from "../../modules/ui/models";
import useLocalStorage from "../../modules/verificationRequest/hooks/useLocalStorage";
import {
  ICompanyVerificationStatusesConfig,
  ICompanyVerificationStatusObject,
} from "../../modules/verificationRequest/network";
import { VerificationStatus } from "../../modules/verificationRequest/utils/getStatus";
import { FunnelIcon, ReloadIcon, SearchIcon, TrashIcon } from "../MuiIcons";
import { InputStyled } from "../SearchInput/styles";
import { ClearButton, FilterButton, FilterPopover, HeadButton } from "./styles";

type initialFilter = {
  [key in VerificationStatus]: false;
};

const getInitialFilter = (statusesConfig: ICompanyVerificationStatusObject) => {
  const initialFilter = {} as initialFilter;
  for (let status in statusesConfig) {
    initialFilter[status as VerificationStatus] = false;
  }
  return initialFilter;
};

const SELECTED_FILTERS = "selectedFilters";
const SELECTED_CATEGORY_FILTERS = "selectedCategoryFilters";

type CategoryFilter = {
  individual: boolean;
  entity: boolean;
};

const getInitialCategoryFilter = (): CategoryFilter => ({
  individual: false,
  entity: false,
});

const getCategoryFilterValue = (selectedCategories: CategoryFilter): string => {
  const selected = Object.entries(selectedCategories)
    .filter(([, checked]) => checked)
    .map(([category]) => category);

  if (selected.length !== 1) {
    return "";
  }

  return selected[0];
};

export const HeadButtons: FC<{
  statusesConfig: ICompanyVerificationStatusesConfig;
  onChangeFilter: (value: string[]) => void;
  onChangeCategoryFilter: (value: string) => void;
  goToVerification: (id: TId, fromFilter?: boolean) => void;
  onReload: () => void;
  firstRender: boolean;
  isAnotherCompany: boolean;
  setIsAnotherCompany: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  statusesConfig,
  onChangeFilter,
  onChangeCategoryFilter,
  onReload,
  goToVerification,
  isAnotherCompany = false,
  setIsAnotherCompany,
  firstRender,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useLocalStorage<initialFilter>(
    SELECTED_FILTERS,
    getInitialFilter(statusesConfig.object),
  );
  const [selectedCategoryFilters, setSelectedCategoryFilters] =
    useLocalStorage<CategoryFilter>(
      SELECTED_CATEGORY_FILTERS,
      getInitialCategoryFilter(),
    );
  const [verificationIdFilter, setVerificationIdFilter] = useState("");

  const handleFilterBtnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFiltersClose = () => {
    setAnchorEl(null);
  };

  const onSaveFilters = useCallback(() => {
    if (verificationIdFilter) {
      goToVerification(verificationIdFilter, true);
      return;
    }
    const newFilters = Object.entries(selectedFilters)
      .filter((elem) => elem[1])
      .map((elem) => elem[0]);
    const selectedCategory = getCategoryFilterValue(selectedCategoryFilters);
    onChangeFilter(newFilters);
    onChangeCategoryFilter(selectedCategory);
    handleFiltersClose();
  }, [
    verificationIdFilter,
    selectedFilters,
    selectedCategoryFilters,
    onChangeFilter,
    onChangeCategoryFilter,
    goToVerification,
  ]);

  useEffect(() => {
    if (firstRender) {
      onSaveFilters();
    }
  }, [onSaveFilters, firstRender]);

  const onClearFilters = () => {
    getInitialFilter(statusesConfig.object);
    setSelectedFilters(getInitialFilter(statusesConfig.object));
    setSelectedCategoryFilters(getInitialCategoryFilter());
    setVerificationIdFilter("");
    onChangeFilter([]);
    onChangeCategoryFilter("");
    setIsAnotherCompany(false);
    handleFiltersClose();
  };

  const handleChangeFilter = (event: any) => {
    setVerificationIdFilter("");
    setIsAnotherCompany(false);
    setSelectedFilters({
      ...selectedFilters,
      [event?.target?.name]: event?.target?.checked,
    });
  };
  const handleChangeCategoryFilter = (event: any) => {
    setVerificationIdFilter("");
    setIsAnotherCompany(false);
    setSelectedCategoryFilters({
      ...selectedCategoryFilters,
      [event?.target?.name]: event?.target?.checked,
    });
  };
  const handleChangeVerificartionIdFilter = (event: any) => {
    setSelectedFilters(getInitialFilter(statusesConfig.object));
    setSelectedCategoryFilters(getInitialCategoryFilter());
    setIsAnotherCompany(false);
    setVerificationIdFilter(event?.target?.value);
  };

  const CheckboxStyled = styled(Checkbox)({
    padding: "4px",
    marginLeft: "4px",
  });

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  return (
    <>
      <Stack direction="row" spacing={1}>
        <HeadButton
          aria-describedby={id}
          variant="text"
          onClick={handleFilterBtnClick}
          startIcon={<FunnelIcon />}
        >
          Filter
        </HeadButton>
        <HeadButton
          variant="text"
          startIcon={<ReloadIcon />}
          onClick={onReload}
        >
          Refresh
        </HeadButton>
      </Stack>
      <FilterPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFiltersClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onKeyDown={(e: any) => {
          if (e.key === KeyboardEventKey.ENTER) onSaveFilters();
        }}
      >
        <Typography sx={{ mb: 1 }} fontWeight="bold">
          Verification ID
        </Typography>
        <InputStyled
          placeholder="Search ID"
          value={verificationIdFilter}
          onChange={handleChangeVerificartionIdFilter}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
        {isAnotherCompany ? (
          <Alert
            sx={{ padding: "4px 12px 4px 8px !important", mt: 1 }}
            severity="warning"
          >
            This verification belongs to another company
          </Alert>
        ) : null}
        <Typography sx={{ mt: 2 }} fontWeight="bold">
          Status
        </Typography>
        <FormGroup
          sx={{
            mb: 2,
            gap: 0.25,
            "& .MuiFormControlLabel-root": { my: -0.25 },
          }}
        >
          {statusesConfig.filters.map((filter) => {
            return (
              <FormControlLabel
                control={<CheckboxStyled />}
                label={filter.displayName}
                name={filter.status}
                checked={selectedFilters[filter.status]}
                onChange={handleChangeFilter}
              />
            );
          })}
        </FormGroup>
        <Typography sx={{ mt: 2 }} fontWeight="bold">
          Category
        </Typography>
        <FormGroup
          sx={{
            mb: 2,
            gap: 0.25,
            "& .MuiFormControlLabel-root": { my: -0.25 },
          }}
        >
          <FormControlLabel
            control={<CheckboxStyled />}
            label="Individual"
            name="individual"
            checked={selectedCategoryFilters.individual}
            onChange={handleChangeCategoryFilter}
          />
          <FormControlLabel
            control={<CheckboxStyled />}
            label="Entity"
            name="entity"
            checked={selectedCategoryFilters.entity}
            onChange={handleChangeCategoryFilter}
          />
        </FormGroup>
        <Stack sx={{ width: 320 }} direction="row" spacing={2}>
          <ClearButton
            variant="contained"
            startIcon={<TrashIcon />}
            onClick={onClearFilters}
          >
            Clear filter
          </ClearButton>
          <FilterButton variant="contained" onClick={onSaveFilters}>
            {verificationIdFilter ? "Go to Verification" : "Save"}
          </FilterButton>
        </Stack>
      </FilterPopover>
    </>
  );
};
